/**
 * PreviewPoolGroupBlock — one allocation pool's rows inside the Paths list.
 *
 * Each pool gets its own bordered card + header so the eye can tell at a
 * glance which paths compete with which. Weights only ever sum within a pool.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FlowOptimizerItemPreview } from '@trafficpulse/shared';

import PreviewLayerLabels from '../PreviewLayerLabels';
import PreviewPathListRow from '../PreviewPathListRow';
import { previewItemKey } from '../preview-sort';
import { toPreviewRow } from '../preview-row.types';

export interface PreviewPoolGroupBlockProps {
  name: string;
  items: ReadonlyArray<FlowOptimizerItemPreview>;
  ranks: ReadonlyMap<string, number>;
  onSelectPath?: (id: string) => void;
  selectedPathId: string | null;
  /** Hide the column header row — only the first block needs it. */
  hideLabels?: boolean;
}

const PreviewPoolGroupBlock: FC<PreviewPoolGroupBlockProps> = ({
  name,
  items,
  ranks,
  onSelectPath,
  selectedPathId,
  hideLabels,
}) => (
  <div className="space-y-1">
    <div className="flex items-baseline justify-between gap-2 px-1">
      <span className="truncate text-[10px] font-medium text-muted-foreground" title={name}>
        {name}
      </span>
      <span className="text-[10px] tabular-nums text-muted-foreground/70">
        {items.length}
      </span>
    </div>
    <div className="overflow-hidden rounded-md border border-border/60">
      {hideLabels ? null : <PreviewLayerLabels />}
      {items.map((item) => (
        <PreviewPathListRow
          key={previewItemKey(item)}
          name={item.itemName?.trim() || item.itemId}
          row={toPreviewRow(item)}
          rank={ranks.get(previewItemKey(item))}
          selected={onSelectPath != null && selectedPathId === item.itemId}
          onSelect={onSelectPath ? () => onSelectPath(item.itemId) : undefined}
        />
      ))}
    </div>
  </div>
);

export default PreviewPoolGroupBlock;
